import { UserPlus, X } from 'lucide-react'
import Image from 'next/image'
import React, { useState } from 'react'

import UserSearch from '@/components/chat/user-search'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'

import { useAuth } from '@/contexts/auth-context'
import { useChat } from '@/contexts/chat-context'

import { Conversation } from '@/types/chat.type'
import { UserProfile } from '@/types/user.type'

interface AddParticipantsDialogProps {
  conversation: Conversation
  onClose: () => void
}

interface SelectedUser {
  id: number
  firstName: string
  lastName: string
  email: string
  avatar?: string
  profileId?: string
}

export const AddParticipantsDialog: React.FC<AddParticipantsDialogProps> = ({ conversation, onClose }) => {
  const { user } = useAuth()
  const { addParticipants, reloadConversations } = useChat()
  const [isLoading, setIsLoading] = useState(false)
  const [selectedUsers, setSelectedUsers] = useState<SelectedUser[]>([])

  // Add null checks to prevent TypeError
  if (!conversation || !conversation.participants) {
    return null
  }

  const handleAddParticipants = async () => {
    if (selectedUsers.length === 0) return

    setIsLoading(true)
    try {
      await addParticipants(
        conversation.id,
        selectedUsers.map(u => u.id)
      )
      setSelectedUsers([])

      // Refresh conversations to show updated participant list
      await reloadConversations()

      onClose()
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to add participants:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleUserSelect = (user: UserProfile) => {
    // Check if user is already a participant
    const isAlreadyParticipant = conversation.participants.some(p => p.user.id === user.id)
    if (isAlreadyParticipant) {
      return
    }

    // Check if user is already selected
    const isAlreadySelected = selectedUsers.some(u => u.id === user.id)
    if (isAlreadySelected) {
      return
    }

    setSelectedUsers(prev => [
      ...prev,
      {
        id: user.id,
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        avatar: user.avatar,
      },
    ])
  }

  const handleUserRemove = (userId: number) => {
    setSelectedUsers(prev => prev.filter(u => u.id !== userId))
  }

  // Get user IDs to exclude from search (already participants + current user)
  const excludeUserIds = [...conversation.participants.map(p => p.user.id), ...(user?.id ? [user.id] : [])]

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5" />
            Add Participants
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* User Search */}
          <div className="space-y-2">
            <div className="text-sm font-medium text-gray-700">Search Users</div>
            <UserSearch
              onUserSelect={handleUserSelect}
              placeholder="Search users to add..."
              excludeUserIds={excludeUserIds}
              maxUsers={20}
            />
          </div>

          {/* Selected Users */}
          {selectedUsers.length > 0 && (
            <div className="space-y-2">
              <div className="text-sm font-medium text-gray-700">Selected Users:</div>
              <div className="flex flex-wrap gap-2">
                {selectedUsers.map(user => (
                  <Badge key={user.id} variant="secondary" className="flex items-center gap-1">
                    <div className="w-4 h-4 rounded-full bg-gray-200 flex items-center justify-center">
                      {user.avatar ? (
                        <Image
                          src={user.avatar}
                          alt={`${user.firstName} ${user.lastName}`}
                          className="w-4 h-4 rounded-full object-cover"
                        />
                      ) : (
                        <span className="text-xs text-gray-600">
                          {user.firstName[0]}
                          {user.lastName[0]}
                        </span>
                      )}
                    </div>
                    <span className="text-xs">
                      {user.firstName} {user.lastName}
                    </span>
                    <button
                      onClick={() => handleUserRemove(user.id)}
                      className="ml-1 hover:text-red-500 transition-colors"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button
              onClick={handleAddParticipants}
              disabled={isLoading || selectedUsers.length === 0}
              className="flex-1"
            >
              {isLoading
                ? 'Adding...'
                : `Add ${selectedUsers.length} Participant${selectedUsers.length !== 1 ? 's' : ''}`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
